import React from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/sections/Footer";
import SEOHead from "@/components/SEOHead";
import EligibilityCheckForm from "@/components/EligibilityCheckForm";
import Breadcrumbs from "@/components/Breadcrumbs";
import { Link } from "@/lib/router-compat";
import {
  Clock,
  Calendar,
  HelpCircle,
  CheckCircle2,
  Activity,
  Zap,
  Timer,
} from "lucide-react";

export default function HowLongDoesTmsTreatmentLastPage() {
  const publishedDate = "2024-05-22";
  const updatedDate = "2026-11-03";
  const heroImage =
    "https://neurowellnesstms.com/wp-content/uploads/2024/05/how-long-does-tms-treatment-last.jpg";

  const faqs = [
    {
      q: "How long does a single TMS treatment session last?",
      a: "A standard TMS session lasts 18–20 minutes. Patients sit in a recliner, fully alert, and can return to work or normal activities immediately afterward.",
    },
    {
      q: "How long is a full course of TMS therapy?",
      a: "A traditional TMS course is 36 sessions over 6 weeks (5 sessions per week). Accelerated SAINT® TMS condenses treatment into 50 sessions over just 5 days.",
    },
    {
      q: "How soon will I feel results from TMS?",
      a: "Most patients notice improvement around weeks 2–3 of a traditional course. With accelerated SAINT® TMS, some patients experience significant mood lift within days.",
    },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEOHead
        title="How Long Does a TMS Treatment Last? Session & Course Timeline | Neuro Wellness"
        description="Learn how long each TMS session lasts (18–20 min), the full 6-week course timeline, and how accelerated SAINT® TMS condenses treatment into 5 days."
        canonical="/how-long-does-a-tms-treatment-last"
        keywords="how long does TMS treatment last, TMS session length, TMS course duration, accelerated TMS timeline, SAINT TMS 5 days Coral Springs"
        ogImage={heroImage}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "MedicalWebPage",
                "@id":
                  "https://neurowellnesstms.com/how-long-does-a-tms-treatment-last#webpage",
                name: "How Long Does a TMS Treatment Last?",
                url: "https://neurowellnesstms.com/how-long-does-a-tms-treatment-last",
                inLanguage: "en-US",
                datePublished: publishedDate,
                dateModified: updatedDate,
                description:
                  "Detailed timeline of TMS therapy including single session duration, full course length, and accelerated SAINT protocol timelines.",
                publisher: {
                  "@type": "MedicalOrganization",
                  name: "Neuro Wellness TMS Centers of America",
                  url: "https://neurowellnesstms.com",
                  telephone: "+1-954-827-9793",
                  address: {
                    "@type": "PostalAddress",
                    streetAddress: "9734 West Sample Road, 2nd Floor, Suite B",
                    addressLocality: "Coral Springs",
                    addressRegion: "FL",
                    postalCode: "33065",
                    addressCountry: "US",
                  },
                },
              },
              {
                "@type": "FAQPage",
                "@id":
                  "https://neurowellnesstms.com/how-long-does-a-tms-treatment-last#faq",
                mainEntity: faqs.map((f) => ({
                  "@type": "Question",
                  name: f.q,
                  acceptedAnswer: { "@type": "Answer", text: f.a },
                })),
              },
            ],
          }),
        }}
      />

      <Navbar />

      <main className="pt-16 sm:pt-20">
        <section className="relative overflow-hidden bg-primary-deep text-primary-foreground py-12 sm:py-16 lg:py-20">
          <div className="absolute inset-0 bg-gradient-to-br from-primary-deep via-primary-deep/95 to-slate-900" />
          <div className="container-px relative mx-auto max-w-6xl">
            <div className="grid items-center gap-8 lg:grid-cols-12 lg:gap-12">
              <div className="order-2 lg:order-1 lg:col-span-7">
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="inline-flex items-center gap-2 rounded-full border border-accent/40 bg-accent/15 px-3.5 py-1 text-xs font-semibold text-accent backdrop-blur-sm">
                    <Timer className="h-4 w-4" />
                    Treatment Timeline
                  </span>
                  <span className="text-xs text-primary-foreground/75 flex items-center gap-1">
                    <Calendar className="h-3.5 w-3.5" />
                    Medically Reviewed · Updated May 2024
                  </span>
                </div>

                <h1 className="font-headings text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight text-white">
                  How Long Does a{" "}
                  <span className="text-accent">TMS Treatment Last?</span>
                </h1>

                <p className="mt-5 text-base sm:text-lg text-primary-foreground/90 leading-relaxed max-w-2xl">
                  Understanding session length, the full treatment course, and
                  how accelerated protocols condense weeks into days.
                </p>
              </div>

              <div className="order-1 lg:order-2 lg:col-span-5">
                <div className="rounded-2xl border border-white/15 bg-white/5 p-6 shadow-2xl backdrop-blur-md">
                  <div className="text-center mb-4">
                    <span className="inline-block rounded-full bg-accent/20 px-3 py-1 text-xs font-semibold text-accent">
                      Free Screening
                    </span>
                    <h3 className="text-lg font-bold text-white mt-1">
                      Check TMS Eligibility
                    </h3>
                  </div>
                  <EligibilityCheckForm />
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-10 bg-background">
          <div className="container-px mx-auto max-w-4xl">
            <Breadcrumbs
              crumbs={[
                { name: "Home", href: "/" },
                { name: "Articles", href: "/articles" },
                {
                  name: "How Long Does TMS Treatment Last",
                  href: "/how-long-does-a-tms-treatment-last",
                },
              ]}
            />

            <article className="prose prose-slate max-w-none dark:prose-invert prose-headings:font-headings prose-headings:text-foreground prose-p:text-foreground/90 prose-p:leading-relaxed mt-6">
              <p className="text-lg leading-relaxed font-medium">
                One of the most common questions patients ask about TMS therapy
                is: "How long will this take?" The answer depends on which
                protocol you choose—traditional 6-week TMS or accelerated SAINT®
                5-day TMS.
              </p>

              <h2>Single Session Duration: 18–20 Minutes</h2>
              <p>
                Each standard TMS session lasts just{" "}
                <strong>18 to 20 minutes</strong>. You sit comfortably in a
                recliner, fully awake and alert, while the electromagnetic coil
                delivers targeted magnetic pulses to your prefrontal cortex. You
                can read, watch TV, or simply relax. After the session, you can
                drive home or return to work immediately—no recovery time
                needed.
              </p>

              <h2>Traditional TMS: 6 Weeks, 36 Sessions</h2>
              <p>
                A full standard TMS course consists of{" "}
                <strong>36 sessions</strong> delivered 5 days per week over{" "}
                <strong>6 weeks</strong>. This gradual approach allows
                neuroplasticity to build cumulatively, with most patients
                noticing mood improvement around weeks 2–3 and full benefits
                emerging after the complete course.
              </p>

              <h2>Accelerated SAINT® TMS: 5 Days, 50 Sessions</h2>
              <p>
                <Link
                  to="/saint"
                  className="text-primary hover:underline font-semibold"
                >
                  SAINT® (Stanford Accelerated Intelligent Neuromodulation
                  Therapy)
                </Link>{" "}
                condenses the entire treatment into{" "}
                <strong>5 consecutive days</strong>, delivering 10 sessions per
                day (50 total). Each session lasts about 10 minutes with
                50-minute rest intervals. Clinical studies show remission rates
                of 79–90%—significantly higher than traditional TMS.
              </p>

              <h2>Which Timeline Is Right for You?</h2>
              <p>
                Traditional TMS suits patients who prefer a gradual approach and
                live locally. SAINT® accelerated TMS is ideal for patients who
                need rapid relief, are traveling from out of town, or have
                severe, acute depression requiring urgent intervention.
              </p>
            </article>

            <div className="my-12 border-t border-border pt-8">
              <h2 className="text-2xl font-bold text-foreground flex items-center gap-2 mb-6">
                <HelpCircle className="h-6 w-6 text-primary" />
                Frequently Asked Questions
              </h2>
              <div className="space-y-4">
                {faqs.map((faq, idx) => (
                  <div
                    key={idx}
                    className="rounded-xl border border-border bg-card p-5"
                  >
                    <h3 className="font-semibold text-foreground text-base sm:text-lg">
                      {faq.q}
                    </h3>
                    <p className="mt-2 text-sm text-foreground/80 leading-relaxed">
                      {faq.a}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div className="my-10 rounded-xl bg-muted/40 p-6 border border-border">
              <h3 className="text-lg font-bold text-foreground mb-3">
                Related TMS Guides
              </h3>
              <ul className="grid sm:grid-cols-2 gap-2 text-sm text-primary font-medium">
                <li>
                  <Link to="/what-is-tms-therapy" className="hover:underline">
                    → What Is TMS Therapy?
                  </Link>
                </li>
                <li>
                  <Link
                    to="/how-tms-therapy-works-coral-springs-fl"
                    className="hover:underline"
                  >
                    → How TMS Therapy Works
                  </Link>
                </li>
                <li>
                  <Link to="/saint" className="hover:underline">
                    → SAINT® Accelerated TMS
                  </Link>
                </li>
                <li>
                  <Link
                    to="/what-can-tms-therapy-treat"
                    className="hover:underline"
                  >
                    → What Can TMS Treat?
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
